import React from 'react';
import { Bookmark, History, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import type { BrowserBookmark, BrowserHistoryEntry } from './types';

const formatTimestamp = (value: string) => {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const SiteRow = ({
  title,
  subtitle,
  url,
  favicon,
  action,
  onOpen,
}: {
  title: string;
  subtitle: string;
  url: string;
  favicon: string | null;
  action?: React.ReactNode;
  onOpen: () => void;
}) => (
  <div className="flex items-center gap-3 rounded-lg border border-border/70 bg-background/70 px-3 py-3">
    {favicon ? (
      <img src={favicon} alt="" className="h-5 w-5 rounded-sm" />
    ) : (
      <div className="flex h-5 w-5 items-center justify-center rounded-sm bg-muted text-[10px] font-semibold text-muted-foreground">
        •
      </div>
    )}
    <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
      <div className="truncate text-sm font-medium text-foreground">{title}</div>
      <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
      <div className="truncate pt-1 text-xs text-primary">{url}</div>
    </button>
    {action}
  </div>
);

export const BrowserLibraryDialog = ({
  open,
  onOpenChange,
  bookmarks,
  historyEntries,
  onNavigate,
  onRemoveBookmark,
  onClearHistory,
  portalContainer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookmarks: BrowserBookmark[];
  historyEntries: BrowserHistoryEntry[];
  onNavigate: (url: string) => void;
  onRemoveBookmark: (bookmarkId: string) => void;
  onClearHistory: () => void;
  portalContainer?: HTMLElement | null;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden" portalContainer={portalContainer}>
        <DialogTitle className="sr-only">Navigator library</DialogTitle>
        <DialogDescription className="sr-only">
          Browse saved bookmarks and browsing history.
        </DialogDescription>
        <Tabs defaultValue="bookmarks" className="flex h-[70vh] flex-col">
          <div className="border-b border-border/70 px-6 py-4">
            <div className="text-xl font-semibold tracking-tight">Navigator Library</div>
            <p className="mt-1 text-sm text-muted-foreground">Your saved bookmarks and recent browsing history.</p>
            <TabsList className="mt-4 grid w-full max-w-sm grid-cols-2">
              <TabsTrigger value="bookmarks">Bookmarks</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="bookmarks" className="mt-0 flex-1 overflow-hidden px-6 py-5">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Bookmark className="h-4 w-4" />
                  Bookmarks
                </CardTitle>
              </CardHeader>
              <CardContent className="h-[calc(70vh-12rem)]">
                {bookmarks.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No bookmarks yet. Use the star button in Navigator to save the current page.
                  </div>
                ) : (
                  <ScrollArea className="h-full pr-3">
                    <div className="space-y-3">
                      {bookmarks.map((bookmark) => (
                        <SiteRow
                          key={bookmark.id}
                          title={bookmark.title}
                          subtitle={`Saved ${formatTimestamp(bookmark.createdAt)}`}
                          url={bookmark.url}
                          favicon={bookmark.favicon}
                          onOpen={() => {
                            onNavigate(bookmark.url);
                            onOpenChange(false);
                          }}
                          action={
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => onRemoveBookmark(bookmark.id)}
                              aria-label={`Remove ${bookmark.title}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          }
                        />
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-0 flex-1 overflow-hidden px-6 py-5">
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <History className="h-4 w-4" />
                  History
                </CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={onClearHistory} disabled={historyEntries.length === 0}>
                  Clear history
                </Button>
              </CardHeader>
              <CardContent className="h-[calc(70vh-12rem)]">
                {historyEntries.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No browsing history yet, or history has been cleared.
                  </div>
                ) : (
                  <ScrollArea className="h-full pr-3">
                    <div className="space-y-3">
                      {historyEntries.map((entry) => (
                        <SiteRow
                          key={entry.id}
                          title={entry.title}
                          subtitle={`Visited ${formatTimestamp(entry.visitedAt)}`}
                          url={entry.url}
                          favicon={entry.favicon}
                          onOpen={() => {
                            onNavigate(entry.url);
                            onOpenChange(false);
                          }}
                        />
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};